import type { EIP1193Provider } from 'viem';
import { injected } from 'wagmi/connectors';

/**
 * Ledger's DApp Browser injects its EIP-1193 provider as `window.ethereum` and
 * announces the same provider over EIP-6963 under the `com.ledger` rdns, so one Ledger
 * session can reach the app as either connector. The provider carries an `isLedgerLive`
 * flag, which is Ledger's documented way of detecting the in-app browser and enabling
 * auto-connection.
 *
 * https://developers.ledger.com/docs/ledger-live/discover/integration/dapp-browser/dapp-customisation
 */

type LedgerProvider = EIP1193Provider & { isLedgerLive?: boolean };

/** Connector id wagmi derives from Ledger's EIP-6963 rdns. */
export const LEDGER_RDNS = 'com.ledger';

/** Id of the connector registered below. */
export const LEDGER_CONNECTOR_ID = 'ledgerLive';

export const isLedgerConnectorId = (id?: string): boolean =>
  id === LEDGER_RDNS || id === LEDGER_CONNECTOR_ID;

/**
 * `window.ethereum`, but only when it is Ledger's. Typed structurally rather than as
 * `Window` so it can be handed straight to wagmi's `injected({ target })` resolver,
 * which supplies its own `Window`.
 */
const getLedgerProvider = (
  target: { ethereum?: LedgerProvider } | undefined,
): LedgerProvider | undefined =>
  target?.ethereum?.isLedgerLive ? target.ethereum : undefined;

export const isLedgerLiveBrowser = (): boolean =>
  Boolean(getLedgerProvider(globalThis.window));

/**
 * Registered first, always.
 *
 * `target` is resolved lazily inside `getProvider()`, so this connector does not care
 * whether Ledger has injected by the time this module evaluates -- in a production
 * build it has not. Outside Ledger it resolves nothing and stays inert; RainbowKit
 * never lists it, since it is not one of its wallets.
 *
 * Order matters. wagmi's mount reconnect probes registered connectors serially with no
 * timeout, and on a cold load -- no persisted connection, no `recentConnectorId` --
 * that is plain registration order. First means Ledger is reached before any
 * WalletConnect wallet can stall, and it claims `window.ethereum` before a RainbowKit
 * injected connector can take the session under its own name.
 */
export const ledgerLiveConnector = injected({
  target: () => ({
    id: LEDGER_CONNECTOR_ID,
    name: 'Ledger Live',
    provider: (window) => getLedgerProvider(window),
  }),
});
